import { inject, Injectable, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanMatchFn, PRIMARY_OUTLET, Router } from '@angular/router';
import { firstValueFrom, of } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';
import { FeatureToggleService } from './feature-toggle.service';
import { PathfinderV2Gate } from './pathfinder-v2-gate';

/**
 * Which version of a module a path serves, decided by the `Pathfinder V2` toggle (72).
 *
 * <h3>Plain routing, one path per module</h3>
 *
 * Every module that exists in both versions (persons, vehicles, organizations, event records, property, charging,
 * Property Room, Property Release) is declared TWICE in `app.routes.ts` under the SAME path: the V2 entry first,
 * behind {@link pathfinderV2CanMatch}, then the V1 entry behind {@link pathfinderV1CanMatch}. Exactly one of them
 * matches, so a URL means the same thing whichever version a tenant runs, and nothing redirects between versions.
 *
 * `canMatch` rather than `canActivate` because it runs BEFORE `loadChildren`: the entry that does not match never
 * downloads its chunk. A V2 tenant never fetches the V1 module, and a V1 tenant never fetches the V2 one.
 *
 * <h3>Why it LOADS the toggles rather than waiting for them</h3>
 *
 * Toggles arrive by HTTP after login, and an empty list means both "not fetched yet" and "nothing enabled".
 * Reading the signal without waiting would answer from the pre-load state on a cold deep link. But waiting alone
 * deadlocked once: the only thing that fetched the toggles was `AppMenuComponent`, inside the app shell, and a
 * `canMatch` guard runs before any component of the route tree exists. So the guard asks for them itself, through
 * the shared, idempotent `ensureTenantFeaturesLoaded`, and waits a bounded time.
 *
 * <h3>When the toggles cannot be read, it is V2</h3>
 *
 * A timeout, a failed fetch, or an offline cold start in the desktop app (which wipes the device's copy of the
 * toggles on close) all answer V2 (Adrian, 2026-10-14). Every tenant runs V2; V1 is a failsafe for tenants that
 * deliberately switched it off, and those are reachable whenever the toggles are.
 */

/**
 * How long a navigation waits for the toggles before deciding without them.
 *
 * Long enough for a normal round trip on a poor link, short enough that an officer is never looking at nothing while
 * wondering whether the app has crashed.
 */
export const TOGGLE_WAIT_MS = 5_000;

/** Decides which version serves the modules that exist twice. Root-provided: the routes and the preloader share it. */
@Injectable({ providedIn: 'root' })
export class PathfinderStackService {
    private readonly featureToggleService = inject(FeatureToggleService);
    private readonly gate = inject(PathfinderV2Gate);
    private readonly router = inject(Router);
    private readonly injector = inject(Injector);

    /** The decision already taken for a navigation, so both entries of a pair share it. */
    private decision: { navigationId: number; answer: Promise<boolean> } | null = null;

    /**
     * Whether V2 serves the paired modules, once the toggles have settled or the wait ran out.
     *
     * ONE DECISION PER NAVIGATION. The V2 entry asks first and may wait; the V1 entry under the same path — reached
     * only when the V2 entry did not match, which includes a V2 module with no route for a child path — gets the same
     * promise back, so it never waits a second time and can never disagree with it.
     */
    public servesV2(): Promise<boolean> {
        const navigationId = this.router.currentNavigation()?.id;
        if (navigationId !== undefined && this.decision?.navigationId === navigationId) {
            return this.decision.answer;
        }
        const answer = this.settled().then(() => this.servesV2Now());
        if (navigationId !== undefined) {
            this.decision = { navigationId, answer };
        }
        return answer;
    }

    /**
     * Whether the toggles are KNOWN to say V2: readable and on. False while they load or when they cannot be read.
     *
     * For anything that is not a route decision — the menu, the problem interceptor. The routes serve V2 when the
     * toggles cannot be read, because they must serve something; a reader that only labels or reports must not, or a
     * tenant with V2 off would see V2 behaviour at every login until its toggles arrive.
     */
    public servesV2Known(): boolean {
        return this.togglesReadable() && this.gate.isEnabled();
    }

    /**
     * Whether the toggle list in hand is a real answer: loaded, and not the empty list a failed fetch leaves.
     * A list restored from the device while offline counts — it is exactly what was last fetched.
     */
    public togglesReadable(): boolean {
        return this.featureToggleService.tenantFeaturesLoadedComputedSignal()
            && !this.featureToggleService.tenantFeaturesFailedComputedSignal();
    }

    /** The answer from what is known now: the gate when the toggles are readable, otherwise V2. */
    public servesV2Now(): boolean {
        return this.togglesReadable() ? this.gate.isEnabled() : true;
    }

    /**
     * Have the toggles, one way or another, before deciding.
     *
     * Both halves are needed. `ensureTenantFeaturesLoaded` covers the cold navigation where nothing has asked yet;
     * the signal wait covers a load someone ELSE started, whose promise cannot be awaited from here.
     *
     * TIMED, and the timeout resolves rather than throws: a guard that never settles is a blank screen with no
     * error, strictly worse than deciding without the answer.
     */
    public async settled(): Promise<void> {
        if (this.featureToggleService.tenantFeaturesLoadedComputedSignal()) {
            return;
        }

        const tenant = this.tenantOfUrl();
        if (tenant) {
            void this.featureToggleService.ensureTenantFeaturesLoaded(tenant);
        }

        // The injector is passed explicitly: this runs after an `await` in its callers, where the ambient
        // injection context is gone.
        await firstValueFrom(
            toObservable(this.featureToggleService.tenantFeaturesLoadedComputedSignal, { injector: this.injector }).pipe(
                filter(Boolean),
                take(1),
                timeout({ first: TOGGLE_WAIT_MS, with: () => of(true) }),
            ),
        );
    }

    /**
     * The tenant of the URL being navigated to — `router.url` is still the previous one while a guard runs — or
     * of the current URL outside a navigation (the preloader). Toggles are scoped to it.
     */
    private tenantOfUrl(): string | null {
        const target = this.router.currentNavigation()?.extractedUrl ?? this.router.parseUrl(this.router.url);
        const segments = target.root.children[PRIMARY_OUTLET]?.segments ?? [];
        const at = segments.findIndex((segment) => segment.path === 't');
        return at >= 0 ? segments[at + 1]?.path ?? null : null;
    }
}

/** On a module's V2 entry: matches when V2 serves it, including when the toggles cannot be read. */
export const pathfinderV2CanMatch: CanMatchFn = () => inject(PathfinderStackService).servesV2();

/**
 * On a module's V1 entry, declared after the V2 one under the same path: matches when V2 does not serve it.
 *
 * The same navigation's decision (see `servesV2`), so it answers at once — the V2 entry has already waited.
 */
export const pathfinderV1CanMatch: CanMatchFn = async () => !(await inject(PathfinderStackService).servesV2());
